import type { CommandResponse, TelephonyEvent, ExtensionState, LiveCallStatus } from '../contract'
import { TelephonyCommandNames } from '../contract'
import type { ConnectionStatus, SendCommand, TelephonySource } from './types'
import { mockVoicemails } from './voicemail'
import { mockCallHistory, type CallHistoryEntry } from './history'

// In-memory mock that emits a believable telephony stream (snake_case wire shapes, per
// contract v1) so the console runs with no backend. Replaced wholesale by SignalRSource
// once the cloud bridge is live.

const EXTENSIONS: Array<{ ext: string; state: ExtensionState }> = [
  { ext: '1040', state: 'not_in_use' }, { ext: '1041', state: 'in_use' },
  { ext: '1042', state: 'not_in_use' }, { ext: '1043', state: 'ringing' },
  { ext: '1044', state: 'on_hold' }, { ext: '1045', state: 'in_use' },
  { ext: '1046', state: 'unavailable' }, { ext: '1047', state: 'not_in_use' },
  { ext: '1048', state: 'not_in_use' }, { ext: '1049', state: 'in_use' }
]

const iso = () => new Date().toISOString()
const pick = <T,>(a: T[]): T => a[Math.floor(Math.random() * a.length)]

export class MockSource implements TelephonySource {
  private eventHandlers = new Set<(e: TelephonyEvent) => void>()
  private statusHandlers = new Set<(s: ConnectionStatus) => void>()
  private _status: ConnectionStatus = 'disconnected'
  private timer: ReturnType<typeof setInterval> | undefined
  private seq = 0

  async start(): Promise<void> {
    this.setStatus('connecting')
    this.emitSnapshot()
    this.timer = setInterval(() => this.tick(), 2500)
    this.setStatus('connected')
  }
  async stop(): Promise<void> {
    if (this.timer) clearInterval(this.timer)
    this.setStatus('disconnected')
  }
  onEvent(handler: (e: TelephonyEvent) => void): () => void {
    this.eventHandlers.add(handler); return () => this.eventHandlers.delete(handler)
  }
  onStatus(handler: (s: ConnectionStatus) => void): () => void {
    this.statusHandlers.add(handler); return () => this.statusHandlers.delete(handler)
  }
  status(): ConnectionStatus { return this._status }

  async sendCommand(cmd: SendCommand): Promise<CommandResponse> {
    if (cmd.command === TelephonyCommandNames.Hold || cmd.command === TelephonyCommandNames.Unhold) {
      const channel = String(cmd.params?.channel ?? 'PJSIP/1042-00000a1')
      this.emit({
        type: 'call_hold', extension: '1042', unique_id: 'mock-call-1', channel,
        on_hold: cmd.command === TelephonyCommandNames.Hold
      })
    }
    if (cmd.command === TelephonyCommandNames.VoicemailList) {
      const mailbox = String(cmd.params?.mailbox ?? '1042')
      return {
        command_id: `mock-${++this.seq}`, command: cmd.command, status: 'success',
        executed_at: iso(), site_guid: 'dev-site', version: '1.0.1',
        result: { messages: mockVoicemails(mailbox) }
      }
    }
    return {
      command_id: `mock-${++this.seq}`, command: cmd.command, status: 'success',
      executed_at: iso(), site_guid: 'dev-site', version: '1.0.1',
      result: { channel_id: String(cmd.params?.channel ?? '') }
    }
  }

  async fetchCallHistory(): Promise<CallHistoryEntry[]> { return mockCallHistory() }

  private emit(e: TelephonyEvent): void { for (const h of this.eventHandlers) h(e) }
  private setStatus(s: ConnectionStatus): void { this._status = s; for (const h of this.statusHandlers) h(s) }

  private emitSnapshot(): void {
    for (const x of EXTENSIONS) this.emit({ type: 'extension_state', extension: x.ext, state: x.state })
    this.emit({
      type: 'call_info', unique_id: 'mock-call-1', channel: 'PJSIP/1042-00000a1', extension: '1042',
      caller_id_name: 'A. Customer', caller_id_number: '+14805550199', direction: 'inbound',
      status: 'connected', connected_line_number: '1042'
    })
    this.emit({
      type: 'call_info', unique_id: 'mock-call-2', channel: 'PJSIP/1041-00000b7', extension: '1041',
      caller_id_name: '', caller_id_number: '8005551234', direction: 'outbound', status: 'ringing',
      connected_line_number: null
    })
    this.emit({
      type: 'parked_call', parking_space: '701', parkee_channel: 'PJSIP/1050-0000c2',
      caller_id_number: '4805550143', caller_id_name: 'R. Diaz', parker_extension: '1040',
      status: 'parked', timeout_seconds: 45
    })
    for (const q of [
      { queue: 'Support', mi: 'PJSIP/1049', name: 'Support', paused: false, status: 'in_use' },
      { queue: 'Support', mi: 'PJSIP/1042', name: 'You', paused: false, status: 'not_in_use' },
      { queue: 'Sales', mi: 'PJSIP/1047', name: 'Sales', paused: true, status: 'not_in_use' }
    ]) {
      this.emit({
        type: 'queue_member', queue: q.queue, member_interface: q.mi, member_name: q.name,
        paused: q.paused, logged_in: true, status: q.status
      })
    }
    this.emit({ type: 'voicemail_count', mailbox: '1042', new_messages: 3, old_messages: 11 })
    this.emit({ type: 'queue_stats', queue: 'Support', calls_waiting: 3, longest_wait_seconds: 130, calls_answered: 47, avg_answer_seconds: 18, service_level: 0.86 })
    this.emit({ type: 'queue_stats', queue: 'Sales', calls_waiting: 0, longest_wait_seconds: 0, calls_answered: 12, avg_answer_seconds: 9, service_level: 0.95 })
    for (const a of [
      { queue: 'Support', agent: 'PJSIP/1049', answered: 23, ring: 6, call: 184 },
      { queue: 'Support', agent: 'PJSIP/1042', answered: 9, ring: 4, call: 142 },
      { queue: 'Sales', agent: 'PJSIP/1047', answered: 12, ring: 7, call: 205 }
    ]) {
      this.emit({
        type: 'agent_stats', queue: a.queue, agent: a.agent,
        calls_answered: a.answered, avg_ring_seconds: a.ring, avg_call_seconds: a.call
      })
    }
  }

  private tick(): void {
    this.seq++
    const x = pick(EXTENSIONS)
    const states: ExtensionState[] = ['not_in_use', 'in_use', 'ringing', 'on_hold', 'busy']
    x.state = pick(states)
    this.emit({ type: 'extension_state', extension: x.ext, state: x.state })
    if (this.seq % 2 === 0) {
      const status: LiveCallStatus = pick(['ringing', 'connected', 'on_hold'])
      this.emit({
        type: 'call_info', unique_id: 'mock-call-2', channel: 'PJSIP/1041-00000b7', extension: '1041',
        caller_id_name: '', caller_id_number: '8005551234', direction: 'outbound', status,
        connected_line_number: status === 'connected' ? '8005551234' : null
      })
    }
    if (this.seq % 3 === 0) {
      const waiting = Math.floor(Math.random() * 6)
      this.emit({ type: 'queue_stats', queue: 'Support', calls_waiting: waiting, longest_wait_seconds: waiting * 25, calls_answered: 47 + this.seq, avg_answer_seconds: 18, service_level: 0.86 })
    }
    // Demo an incoming ringer (drives the caller-ID screen-pop), then clear it next cycle.
    if (this.seq % 4 === 3) {
      this.emit({
        type: 'call_info', unique_id: 'mock-call-3', channel: 'PJSIP/1042-0000d5', extension: '1042',
        caller_id_name: pick(['M. Reyes', 'Acme Supply', 'J. Okafor', 'Dr. Lin']), caller_id_number: '+14805550147',
        direction: 'inbound', status: 'ringing', connected_line_number: null
      })
    } else if (this.seq % 4 === 0) {
      this.emit({
        type: 'call_info', unique_id: 'mock-call-3', channel: 'PJSIP/1042-0000d5', extension: '1042',
        caller_id_name: '', caller_id_number: '+14805550147', direction: 'inbound', status: 'ended',
        connected_line_number: null
      })
    }
  }
}
